/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { PRODUCT_NAME, ROUTES } from 'constants';

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Button } from 'carbon-addons-iot-react';

import Hero from './components/Hero';
import Section from './components/Section';
import Footer from './components/Footer';

import './components/svg/analytics.svg';
import './components/svg/blockchain.svg';
import './components/svg/connect.svg';
import './components/svg/storage.svg';
import './components/svg/userregistration.svg';
import './components/svg/monitor.svg';

import { MessageBox } from 'Common';

const messages = defineMessages({
  'section.title': 'Need more information?',
  'section.primaryAction': 'Product documentation',
  'section.secondaryAction': 'Contact IBM',
  'section.mainText':
    'Need more information? Browse the documentation to learn more about getting started, product architecture, and how to access developer resources. If you still have questions, contact us, and we will help you find what you need.',
  'hero.title': 'Welcome to {productName}',
  'hero.mainText':
    '{productName} enables AI-powered remote asset monitoring at enterprise scale for asset maintenance and operations leaders seeking to optimize operating performance with fewer, more accurate alerts, and greater insights into what is causing failure.',
  'hero.secondaryAction': 'Learn more',
  'footer.leftPart': 'Copyright © 2020, 2023 IBM',
  'footer.rightPart': 'Submit feedback',
});

const propTypes = {
  errorMessage: PropTypes.string,
  errorMessageDetails: PropTypes.string,
  tenantId: PropTypes.string,
  title: PropTypes.string,
  docURL: PropTypes.string,
  errorMessageType: PropTypes.string,
};

const defaultProps = {
  errorMessage: null,
  errorMessageDetails: null,
  docURL: null,
  tenantId: null,
  title: PRODUCT_NAME,
  errorMessageType: 'info',
};

const WelcomePage = ({
  errorMessage,
  errorMessageDetails,
  errorMessageType,
  tenantId,
  title,
  docURL,
  intl: { formatMessage },
}) => {
  // Building up the hero
  const primActionHero = (
    <Button
      className="leadPrimaryButton"
      href={tenantId ? ROUTES.LOGIN : undefined}
      disabled={!tenantId}>
      {tenantId ? (
        <FormattedMessage defaultMessage="Log in to {tenantId}" values={{ tenantId }} />
      ) : (
        <FormattedMessage defaultMessage="Log in" />
      )}
    </Button>
  );

  const secActionHero = (
    <Button
      kind="ghost"
      className="leadGhostButton"
      href="https://www.ibm.com/products/ibm-maximo-asset-performance-management/asset-monitor?mhsrc=ibmsearch_a&mhq=Maximo%20Asset%20Monitor">
      {formatMessage(messages['hero.secondaryAction'])}
    </Button>
  );

  // Building up the section
  const primActionSection = (
    <Button href={docURL} kind="secondary">
      {formatMessage(messages['section.primaryAction'])}
    </Button>
  );

  const secActionSection = (
    <Button kind="ghost" href="https://www.ibm.com/account/reg/us-en/signup?formid=MAIL-iot">
      {formatMessage(messages['section.secondaryAction'])}
    </Button>
  );

  return (
    <div className="container">
      {errorMessage ? (
        <div className="messageBoxContainer">
          <MessageBox
            type={errorMessageType}
            className="messageBox"
            data={{ message: errorMessage, details: errorMessageDetails }}
            hideCloseButton={false}
          />
        </div>
      ) : null}
      <Hero
        title={formatMessage(messages['hero.title'], {
          productName: title,
        })}
        primaryAction={primActionHero}
        secondaryAction={secActionHero}>
        {formatMessage(messages['hero.mainText'], {
          productName: title,
        })}
      </Hero>
      <Section
        title={formatMessage(messages['section.title'])}
        primaryAction={primActionSection}
        secondaryAction={secActionSection}>
        {formatMessage(messages['section.mainText'])}
      </Section>
      <Footer
        leftPart={formatMessage(messages['footer.leftPart'])}
        rightPart={formatMessage(messages['footer.rightPart'])}
        feedbackLink="https://ibm-watson-iot.ideas.aha.io"
      />
    </div>
  );
};

WelcomePage.propTypes = propTypes;
WelcomePage.defaultProps = defaultProps;
export default injectIntl(WelcomePage);
